<template>
    <section
        ref="section"
        :class="['slice slice-hero slice-hero--default', {'slice-hero--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="hero"
    >
        <div class="slice-content">
            <MktTitleBrand :field="slice.primary.title" class="'slice-hero--default__title'" data-testid="slice-hero-title" />
            <div v-if="slice.primary.subtitle" class="slice-hero--default__subtitle" data-testid="slice-hero-subtitle">
                {{ slice.primary.subtitle }}
            </div>
            <div
                v-if="Array.isArray(slice.primary.description) && isFilled.richText(slice.primary.description)"
                class="slice-hero--default__description"
                data-testid="slice-hero-description"
            >
                <PrismicRichText :field="slice.primary.description" />
            </div>
            <div v-if="buttons.length" class="slice-hero--default__ctas">
                <div v-for="(button, idx) in buttons" :key="idx" :class="`slice-hero--default__cta-wrapper--${button.variant}`">
                    <VJoyButton :href="$withLocalePrefix(button.url)" :variant="button.variant" data-component-source="hero">
                        {{ button.text }}
                    </VJoyButton>
                </div>
            </div>
        </div>
    </section>
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {HeroSlice, HeroSliceDefault} from '@prismicio-types';
    import {MktTitleBrand} from '#components';
    import {VJoyButton} from '@maltjoy/core-vue';

    const props = defineProps(getSliceComponentProps<HeroSlice>());
    const slice = computed(() => props.slice as HeroSliceDefault);

    type CtaButton = {url: string; text: string; variant: 'primary' | 'secondary' | 'main' | undefined};

    const buttons = computed<CtaButton[]>(() =>
        (slice.value.primary.buttons ?? [])
            .filter((button) => isFilled.link(button.cta))
            .map((button) => {
                const cta = button.cta as {url: string; text?: string; variant?: 'primary' | 'secondary' | 'main'};
                return {url: cta.url, text: cta.text ?? '', variant: cta.variant};
            }),
    );
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-hero {
        &--default {
            --hero-header-height: 590px;
            --hero-header-nav-height: 72px;
            --hero-title-font-size: 48px;
            --hero-subtitle-font-size: 24px;
            display: flex;
            align-items: center;

            background-color: var(--joy-color-neutral-0);
            background-image: url('../../../../assets/svg/home-page-hero-mobile-shapes.svg');
            background-repeat: no-repeat;
            background-position: top right;
            background-size: contain;
            padding-top: 21px;

            min-height: var(--hero-header-height);
            height: max-content;

            &__title.title-brand {
                font-size: var(--hero-title-font-size);
                line-height: var(--joy-line-height-small);
            }

            &__subtitle {
                font-size: var(--hero-subtitle-font-size);
                font-family: var(--joy-font-family-title);
                margin-bottom: var(--joy-core-spacing-2);
                line-height: var(--joy-line-height-small);
                color: var(--joy-color-neutral-60);
            }

            &__description {
                color: var(--joy-color-neutral-60);

                a {
                    text-decoration: underline;
                }
            }

            &__ctas {
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: var(--joy-core-spacing-5);
                padding-top: 70px;

                @include mq.screen_xs(min) {
                    flex-direction: row;
                    padding-bottom: var(--joy-core-spacing-5);
                }

                .joy-button {
                    width: 100%;

                    @include mq.screen_sm(min) {
                        width: auto;
                    }
                }
            }

            & &__cta-wrapper--primary {
                .joy-button {
                    color: var(--joy-color-primary-50);
                    background-color: var(--joy-color-neutral-0);

                    &:hover {
                        background-color: var(--joy-color-neutral-20);
                    }
                }
            }

            & &__cta-wrapper--secondary {
                .joy-button {
                    color: var(--joy-color-neutral-0);
                    background-color: transparent;
                    border-color: var(--joy-color-neutral-0);

                    &:hover {
                        border-color: var(--joy-color-neutral-0);
                        background-color: var(--joy-color-neutral-0);
                        color: var(--joy-color-primary-50);
                    }
                }
            }

            @include mq.screen_xs(min) {
                --hero-header-height: 500px;
            }

            @include mq.screen_sm(min) {
                --hero-header-height: 550px;

                &__title {
                    h1 {
                        text-indent: 0.85em hanging;
                    }
                }

                &__description {
                    font-size: var(--joy-font-size-primary-800);
                    line-height: var(--joy-line-height-large);
                    font-weight: 400;
                    max-width: 90%;
                    padding-bottom: 70px;
                }
            }

            @include mq.screen_lg(min) {
                --hero-header-height: 590px;
                --hero-title-font-size: 85px;
                --hero-subtitle-font-size: 52px;

                .slice-content {
                    padding-top: 90px;
                }
            }
        }
    }
</style>
